import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { GetAvailableManagedAttributeHTTPResponse, ViewConfig } from '@bringg/common-utils';
import _ from 'lodash';
import i18next from 'i18next';
import { ApplicationUuid } from '@bringg/types';
import { getSharedRootStore, hasFeatureFlag, RootStore } from '@bringg-frontend/global-stores';
import { getRoot } from 'mobx-easy';
import { TaskManagedAttributeUpdateRealTimeEvent } from '@bringg/dashboard-sdk';

export type ManagedAttributeMetadata = GetAvailableManagedAttributeHTTPResponse['attributes']['task'][number];

export { default as ManagedAttributeCell } from './lib/managed-attribute-cell';
export { getManagedAttributeValue } from './lib/managed-attribute-cell';
export { default as useMeetup, type MeetupTaskData } from './lib/hooks/use-meetup';
export { meetupProvider } from './lib/data/meetup-provider';
export { MeetupIcon } from './lib/icons/meetup/meetup-icon';

function getStore() {
	return getRootEnv().dashboardSdk.sdk.v2.taskManagedAttributes;
}

const isTaskManagedAttributesInstalled = () => {
	const { usersStore } = getRoot<RootStore>().data;
	const shouldBlockApp = hasFeatureFlag(usersStore.currentUser, 'should_verify_that_app_in_installed');

	if (!shouldBlockApp) {
		return true;
	}
	return getSharedRootStore().data.applicationStore.isApplicationInstalled(ApplicationUuid.TaskManagedAttributes);
};

const emtyResponse = {
	attributes: {
		task: []
	}
};

function getAll() {
	return getStore().getAll();
}

function onUpdate(callback: (event: TaskManagedAttributeUpdateRealTimeEvent) => void) {
	return getStore().onUpdate(callback);
}

async function fetchAttributes() {
	if (!isTaskManagedAttributesInstalled()) {
		return emtyResponse;
	}
	return await getStore().getAvailableAttributes();
}

async function loadAttributeForTask(taskId: number) {
	if (!isTaskManagedAttributesInstalled()) {
		return;
	}
	await getStore().loadByIds([taskId], { useCache: true });
}

async function loadAttributeForTasks(taskIds: number[]) {
	if (!isTaskManagedAttributesInstalled()) {
		return;
	}
	await getStore().loadByIds(taskIds, { useCache: true });
}

function getAttributeByName<T>(taskId: number, name: string): T | undefined {
	return getStore().get(taskId)?.[name] as T | undefined;
}

function getNameForAttribute(attribute: ManagedAttributeMetadata) {
	if (attribute.display_name) {
		return attribute.display_name;
	}

	const identifierKey = _.snakeCase(attribute.identifier).toUpperCase();

	return i18next.t(`MANAGED_ATTRIBUTES.IDENTIFIER.${identifierKey}.TITLE`);
}

async function fetchAvailableAttributes(tasksIds: number[]) {
	if (!isTaskManagedAttributesInstalled()) {
		return emtyResponse;
	}
	const availableAttributes = await getStore().fetchAvailableAttributes(tasksIds);
	return availableAttributes;
}

function isVisibleInUI(
	attribute: ManagedAttributeMetadata,
	context: keyof ManagedAttributeMetadata['ui']['contexts']
): boolean {
	return attribute.ui?.contexts?.[context]?.visible ?? attribute.ui?.default?.visible ?? attribute.visible_in_the_ui;
}

function getFormatterForAttribute(
	attribute: ManagedAttributeMetadata,
	context: keyof ManagedAttributeMetadata['ui']['contexts']
): ViewConfig['formatter'] {
	return attribute.ui?.contexts?.[context]?.formatter ?? attribute.ui?.default?.formatter ?? 'string';
}

function formatManagedAttributeValue({
	attribute,
	context,
	value
}: {
	attribute: ManagedAttributeMetadata;
	context: keyof ManagedAttributeMetadata['ui']['contexts'];
	value: unknown;
}): string {
	const formatter = getFormatterForAttribute(attribute, context);

	let displayValue: string;

	switch (formatter) {
		case 'presence':
			displayValue = value
				? i18next.t('MANAGED_ATTRIBUTES.FORMATTER.PRESENCE.EXISTS')
				: i18next.t('MANAGED_ATTRIBUTES.FORMATTER.PRESENCE.NOT_EXISTS');
			break;
		case 'string':
		default:
			displayValue = `${value === undefined ? '' : value}`;
	}

	return displayValue;
}

export const ManagedAttributesService = {
	onUpdate,
	getAll,
	loadAttributeForTask,
	loadAttributeForTasks,
	getAttributeByName,
	fetchAttributes,
	getNameForAttribute,
	fetchAvailableAttributes,
	isVisibleInUI,
	getFormatterForAttribute,
	formatManagedAttributeValue
};
